import { Employee } from "domain/entities/employee";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";
import Spinner from "presentation/components/Shared/Spinner";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import JobTitleDropdown from "./JobTitleDropdown";
import TripsheetSettingForm from "./TripsheetSettingForm";
import HeaderTripSettingEmployeeGrid from "./HeaderTripSettingEmployeeGrid";
import { useFilterEmployeeTripsheetStore } from "presentation/stores/use-filter-employee-tripsheet-store";

const TripsheetSettingsGrid: React.FC = () => {
  const { filters, setFilters } = useFilterEmployeeTripsheetStore();
  const toast = useRef<Toast>(null);
  const [showModal, setShowModal] = useState(false);
  const { employee, setEmployee } = useEmployeeStore();

  const [lazyParams, setLazyParams] = useState({
    sortBy: "employeeNumber",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: employees,
    isLoading,
    isError,
    error,
    refetch,
  } = useEmployeesByFilter({
    isActive: true,
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as EmployeeInputFilter);

  const handleCloseModal = () => {
    setShowModal(false);
    refetch();
  };

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail:
        translateRegexMessage(error?.message, "pt") ||
        "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const showActionOptions = (rowData: Employee) => {
    return (
      <div>
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => {
            setEmployee(rowData);
            setShowModal(true);
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={employees?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={employees?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) => {
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          });
        }}
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderTripSettingEmployeeGrid />}
        rowsPerPageOptions={[2, 10, 25, 50]}
        paginatorLeft
        dataKey="employeeNumber"
        globalFilterFields={[
          "employeeNumber",
          "name",
          "contractType",
          "jobTitle",
          "kmValueMonth",
        ]}
      >
        <Column
          field="employeeNumber"
          header={translateText("Number", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
          // body={photo}
          // filterField="name"
          // showFilterMatchModes={false}
        />
        <Column
          field="jobTitle.name"
          header={translateText("Function", "pt")}
          sortable
          filter
          filterField="jobTitle"
          body={(rowData) => rowData.jobTitle?.name}
          showFilterMatchModes={false}
          filterElement={(options) => <JobTitleDropdown options={options} />}
        />

        <Column
          field="kmValueMonth"
          header={translateText("Monthly amount", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
          body={(rowData) =>
            rowData.kmValueMonth !== undefined
              ? rowData.kmValueMonth.toLocaleString("pt-PT", {
                  style: "currency",
                  currency: "EUR", // ou "BRL" para reais
                })
              : "-"
          }
        />
        {/*
        <Column
          body={(rowData) => rowData.kmValueMonth * 12}
          header={translateText("Yearly amount", "pt")}
          sortable
          showFilterMatchModes={false}
        />
        */}
        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Dialog
        header="Configuração Mapa de KMs"
        visible={showModal}
        style={{ width: "80vw" }}
        onHide={handleCloseModal}
      >
        <TripsheetSettingForm isDisable={false} />
      </Dialog>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default TripsheetSettingsGrid;
